import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 25000 });

/** True if `e` is an aborted/cancelled HTTP request — skip user-facing toasts for those. */
export const isAbortedRequestError = (e) => {
  if (!e) return false;
  if (axios.isCancel?.(e)) return true;
  const code = e.code || e?.cause?.code;
  if (code === 'ERR_CANCELED' || code === 'ECONNABORTED') return true;
  const msg = (e.message || '').toLowerCase();
  return msg === 'canceled' || msg.includes('aborted') || msg.includes('network error');
};

export const getHealth = () => api.get('/health').then(r => r.data);
export const geoSearch = (q) => api.get('/geo/search', { params: { q, limit: 8 } }).then(r => r.data.results);
export const geoReverse = (lat, lon) => api.get('/geo/reverse', { params: { lat, lon } }).then(r => r.data);
export const getDashboard = (lat, lon, base = 'USD', targets = 'EUR,GBP,JPY,INR') =>
  api.get('/dashboard', { params: { lat, lon, base, targets } }).then(r => r.data);
export const getPlaces = (lat, lon, category, radius = 2000, limit = 15) =>
  api.get('/places', { params: { lat, lon, category, radius, limit } }).then(r => r.data);
export const convertCurrency = (from, to, amount) =>
  api.get('/currency/convert', { params: { from, to, amount } }).then(r => r.data);
export const getCurrencyRates = (base = 'USD', symbols) =>
  api.get('/currency/rates', { params: symbols ? { base, symbols } : { base } }).then(r => r.data);
export const getCurrencyTimeseries = (from, to, days = 30) =>
  api.get('/currency/timeseries', { params: { from, to, days } }).then(r => r.data);
export const translate = (text, target_lang, source_lang) =>
  api.post('/translate', { text, target_lang, source_lang }).then(r => r.data);
export const getTranslateLanguages = () =>
  api.get('/translate/languages').then(r => r.data.languages);
export const getAlerts = (lat, lon) => api.get('/alerts', { params: { lat, lon } }).then(r => r.data);
export const getArrival = (lat, lon, base = 'USD') =>
  api.get('/arrival', { params: { lat, lon, base } }).then(r => r.data);
export const getNews = (lat, lon) =>
  api.get('/news', { params: { lat, lon } }).then(r => r.data);
export const getTransit = (lat, lon, radius = 800) =>
  api.get('/transit/nearby', { params: { lat, lon, radius } }).then(r => r.data);
export const getHolidays = (countryCode) =>
  api.get('/holidays', { params: { country_code: countryCode } }).then(r => r.data);

// AI Travel Assistant
export const aiChat = (payload) => api.post('/ai/chat', payload).then(r => r.data);
export const aiGetSession = (sessionId) =>
  api.get(`/ai/sessions/${encodeURIComponent(sessionId)}`).then(r => r.data);
export const aiClearSession = (sessionId) =>
  api.delete(`/ai/sessions/${encodeURIComponent(sessionId)}`).then(r => r.data);

/**
 * Stream a chat reply via Server-Sent Events.
 * Calls onToken(delta) for each token chunk and onDone({reply,provider,model}) when complete.
 * Returns an AbortController that callers can use to cancel the stream.
 */
export const aiChatStream = (payload, { onToken, onDone, onError } = {}) => {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 160)}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events separated by blank line
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data:'));
          if (!line) continue;
          const dataStr = line.slice(5).trim();
          if (!dataStr) continue;
          let obj;
          try { obj = JSON.parse(dataStr); } catch { continue; }
          if (obj.type === 'token' && obj.content) onToken?.(obj.content);
          else if (obj.type === 'done') onDone?.(obj);
          else if (obj.type === 'error') onError?.(new Error(obj.message || 'stream error'));
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      onError?.(e);
    }
  })();
  return controller;
};

// Cost + Safety
export const getCost = (countryCode) =>
  api.get('/cost', { params: { country_code: countryCode } }).then(r => r.data);
export const getSafety = (countryCode, cityName) =>
  api.get('/safety', { params: { country_code: countryCode, city_name: cityName } }).then(r => r.data);

// eSIM + Hospitals + Live Local Info
export const getEsim = (countryCode) =>
  api.get('/esim', { params: { country_code: countryCode } }).then(r => r.data);
export const getHospitalsNearby = (lat, lon, limit = 6) =>
  api.get('/hospitals/nearby', { params: { lat, lon, limit } }).then(r => r.data);
export const getLiveLocal = (lat, lon) =>
  api.get('/live-local', { params: { lat, lon } }).then(r => r.data);

// Share
export const shareCreate = (payload) => api.post('/share/itinerary', payload).then(r => r.data);
export const shareGet = (sid) => api.get(`/share/itinerary/${encodeURIComponent(sid)}`).then(r => r.data);
